<template>
  <b-row>
    <b-col>
      <b-row
        style="height: 100px;
        display: flex;"
        v-if="multiple || files.length === 0 &&  startUploadInternalTimer === 0">
        <b-col class="text-center">
        <div class="drop"
             @click="openSelectDialog"
             @dragover.prevent
             @drop="drop">
          Click or Drag and Drop files here
          <input
              v-show="false"
              ref="fileInput"
              @change="onFileChange"
              :accept="accept"
              :multiple="multiple"
              type="file">
          <font-awesome-icon icon="upload"/>
        </div>
        </b-col>
      </b-row>
      <b-row v-if="files.length">
        <b-col class="mt-3">
          <b-row v-for="(file, index) in files" :key="index">
            <b-col cols="7">
              <font-awesome-icon
                :icon="iconFileType(file)"
                v-b-tooltip.hover
                class="pointer-cursor text-anchor"
                :title="file.type"
              />
              {{file.name}}
            </b-col>
            <b-col cols="3 text-muted text-centered">
              <small>{{ formatBytes(file.size, 2) }}</small>
            </b-col>
            <b-col cols="2 text-right" v-if="startUploadInternalTimer === 0">
              <font-awesome-icon
                icon="trash"
                v-b-tooltip.hover
                title="Remove File"
                class="remove-file text-danger"
                @click="files.splice(index, 1)"
              />
            </b-col>
          </b-row>
          <hr>
        </b-col>
      </b-row>
      <error-display :errors="errors" />
      <b-row v-if="percentCompleted">
        <b-col>
          <b-progress :value="percentCompleted" :max="100" show-progress animated></b-progress>
        </b-col>
      </b-row>
      <submit-button
        v-if="files.length > 0 && showSubmitButton && startUploadInternalTimer === 0"
        title="Submit"
        @submit="send(urlUpload)"
      />
      <cancel-upload
        v-if="showCancelUploadButton"
        @cancel="cancel"
      ></cancel-upload>
    </b-col>
  </b-row>
</template>

<script>
import helper from '@/libs/helper';
import CancelUpload from '@/components/utils/upload/CancelUpload';
import uploadValidatorMixin from '@/components/utils/upload/uploadValidatorMixin';
import ErrorDisplay from '@/components/utils/ErrorDisplay';
import SubmitButton from '@/components/utils/buttons/form/SubmitButton';
import { RELEASE_INTERFACE } from '@/vuex/types/actions';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faTrash,
  faFileContract, faFilePdf,
  faFileImage, faFileArchive,
} from '@fortawesome/free-solid-svg-icons';


library.add([faTrash, faFilePdf, faFileImage, faFileContract, faFileArchive]);


export default {
  name: 'upload',
  mixins: [uploadValidatorMixin],
  components: {
    CancelUpload,
    ErrorDisplay,
    SubmitButton,
  },
  props: {
    showSubmitButton: { default: true },
    accept: { default: null }, // null = accept all files type
    urlUpload: { default: '' },
    multiple: { default: true }, // enable multi files upload
    params: { default() { return {}; } }, // extra params to include on upload
    startUpload: { default: false }, // to enable UI changes when upload starts outside component
  },
  data() {
    return {
      files: [],
      percentCompleted: 0,
      timeThresholdToCancelUpload: 30,
      startUploadInternalTimer: 0,
      showCancelUploadButton: false,
      internalParams: {}, // used to concat params and send to upload
    };
  },
  methods: {
    formatBytes(fileSize, decimalPlaces) {
      return helper.formatBytes(fileSize, decimalPlaces);
    },
    iconFileType(file) {
      if (file.type.indexOf('pdf') > -1) return 'file-pdf';
      if (file.type.indexOf('image') > -1) return 'file-image';
      if (file.type.indexOf('compressed') > -1) return 'file-archive';
      if (file.type.indexOf('zip') > -1) return 'file-archive';
      return 'file-contract';
    },
    onFileChange(event) {
      this.clearErrors();
      const files = Array.from(event.target.files);
      if (this.multiple) {
        files.forEach(f => this.processFile(f));
      } else if (files && files.length > 0) {
        this.processFile(files[0]);
      }

      this.$refs.fileInput.value = '';
      this.$emit('change', files);
    },
    openSelectDialog(event) {
      this.clearErrors();
      this.$refs.fileInput.click();
      this.$emit('click', event);
    },
    processFile(file) {
      const validFiles = [];
      if (this.validate(file)) {
        validFiles.push(file);
      }

      this.files = validFiles;
      this.$emit('files-changed', this.files);
    },
    reset() {
      this.files = [];
      this.percentCompleted = 0;
      this.startUploadInternalTimer = 0;
      this.$refs.fileInput.value = '';
    },
    onUploading(percentCompleted) {
      this.percentCompleted = percentCompleted;
      const elapsedTime = (Date.now() - this.startUploadInternalTimer) / 1000;
      if (elapsedTime > this.timeThresholdToCancelUpload) {
        this.showCancelUploadButton = true;
        this.$store.dispatch(RELEASE_INTERFACE);
      }
      return this.$emit('onUploading', percentCompleted);
    },
    drop(event) {
      event.stopPropagation();
      event.preventDefault();
      this.processFile(event.dataTransfer.files[0]);
    },
    send(url) {
      this.startUploadTimer();
      this.percentCompleted = 0;
      if (this.files) {
        this.internalParams = {
          file: this.files.length === 1 ? this.files[0] : this.files,
        };
        Object.keys(this.params).forEach((key) => {
          this.internalParams[key] = this.params[key];
        });
        this.$emit('upload');
        this.$reqs.upload(url, this.internalParams, this.onUploading)
          .then((response) => {
            this.$emit('success', response.data);
            this.reset();
          })
          .catch((error) => {
            this.reset();
            this.$emit('error', error.data);
          });
      }
    },
    cancel() {
      this.$reqs.cancelUploadRequest();
      this.$emit('cancel');
    },
    startUploadTimer() {
      this.startUploadInternalTimer = Date.now();
    },
  },
  watch: {
    startUpload() {
      this.startUploadTimer();
    },
  },
};
</script>

<style lang="scss" scoped>
.row-file-upload {
  align-items: flex-end;
}

.remove-file {
  cursor: pointer;
  color: #3e88c9;
  &:hover {
      background-position: 0 100%;
      color: #0904ff;
    }
}
.file-upload {
    cursor: pointer;
    transition: all 1s;
    font-size: 20px;
    color: #3e88c9;
}

.drop {
  background-color: #f2f2f2;
  border: 2px dashed #ccc;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-flow: column;
  border-radius: 2px;
  cursor: pointer;
  height: 100%;
  margin: 0 auto;
  max-height: 400px;
  width: 100%;

  svg {
    color: #3e88c9;
  }
}

.error-item {
  word-wrap: break-word;      /* IE */
}
</style>



// WEBPACK FOOTER //
// src/components/utils/upload/Upload.vue